import React, { useEffect, useState } from 'react';
import { Loader, Text } from 'src/@vymo/ui/atoms';
import { useAppSelector } from 'src/store/hooks';
import { getError, getLoading, getTestResults } from './selectors';
import TestList from './testList';
import TestSuiteList from './testSuiteList';
import { TestSuite } from './types';
import styles from './index.module.scss';

// eslint-disable-next-line complexity
function VisualTesting({ prId }: { prId?: string }) {
  const isStorybookVisualTest = prId?.endsWith('_sbVt');

  const results = useAppSelector((state) =>
    getTestResults(prId as string)(state),
  );

  const [selectedSuite, setSelectedSuite] = useState<TestSuite | null>(
    isStorybookVisualTest ? results?.suites?.[0] : null,
  );

  useEffect(() => {
    if (isStorybookVisualTest && results?.suites?.length) {
      setSelectedSuite(results.suites[0]);
    } else {
      setSelectedSuite(null);
    }
  }, [isStorybookVisualTest, results?.suites]);

  const isLoading = useAppSelector(getLoading);
  const error = useAppSelector(getError);

  const handleSuiteSelect = (suite: TestSuite) => {
    setSelectedSuite(suite);
  };

  if (!prId) {
    return (
      <div className={styles.visualTesting__empty}>
        <Text type="h5">Please select a PR to view visual test results</Text>
      </div>
    );
  }

  if ((!results && !isLoading) || error) {
    return (
      <div className={styles.visualTesting__empty}>
        <Text type="h5" variant="error">
          No test results found for PR {prId}.
        </Text>
        <Text>
          If the PR is still open, it is possible that the screenshot data has
          been deleted. Rerun the pipeline.
        </Text>
        <Text>
          We retain data for 15 days from the last update or commit to the PR.
        </Text>
      </div>
    );
  }

  const {
    commit,
    total,
    totalPassed,
    totalFailed,
    suites = [],
  } = results ?? {};

  const approvedTest = suites.reduce(
    (count, suite) =>
      count + suite.tests.filter((test) => test.approved).length,
    0,
  );

  const suiteTests = selectedSuite
    ? suites.find((suite) => suite.name === selectedSuite.name)?.tests || []
    : [];

  return (
    <div className={styles.visualTesting}>
      <Loader visible={isLoading}>
        <div className={styles.visualTesting__header}>
          <div className={styles.visualTesting__header__main}>
            <Text type="h4">
              {isStorybookVisualTest
                ? 'Design library Visual Test'
                : 'Visual Test Results'}
            </Text>
            <Text type="sublabel">
              PR{' '}
              <Text
                type="sublabel"
                link={`https://bitbucket.org/vymo/vymo-web-platform/pull-requests/${prId}`}
              >
                {prId}
              </Text>
              - {commit}
            </Text>
          </div>
          <div className={styles.visualTesting__header__stats}>
            <div className={styles.visualTesting__header__stat}>
              <Text type="label">Total Screenshots</Text>
              <Text type="h6">{total}</Text>
            </div>
            <div className={styles.visualTesting__header__stat}>
              <Text type="label">Passed</Text>
              <Text type="h6" variant="success">
                {totalPassed}
              </Text>
            </div>
            <div className={styles.visualTesting__header__stat}>
              <Text type="label">Failed</Text>
              <Text type="h6" variant="error">
                {totalFailed}
              </Text>
            </div>
            <div className={styles.visualTesting__header__stat}>
              <Text type="label">Approved</Text>
              <Text type="h6" variant="info">
                {approvedTest}
              </Text>
            </div>
          </div>
        </div>

        <div className={styles.visualTesting__body}>
          {!isStorybookVisualTest && (
            <div className={styles.visualTesting__sidebar}>
              <TestSuiteList
                suites={suites}
                onSelectSuite={handleSuiteSelect}
              />
            </div>
          )}
          <div className={styles.visualTesting__content}>
            {selectedSuite ? (
              <TestList
                tests={suiteTests}
                prId={prId}
                commitHash={commit as string}
                isStorybookVisualTest={isStorybookVisualTest}
              />
            ) : (
              <div className={styles.visualTesting__empty}>
                <Text type="h5">Select a test suite to view tests</Text>
              </div>
            )}
          </div>
        </div>
      </Loader>
    </div>
  );
}

export default VisualTesting;
